import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { toast } from 'react-toastify';

import { db } from '../../../../firebaseConfig';
import { fetchActiveSeasonDoc } from '../../../../utils/seasonApi';
import { getSeasonSelection, getSeasonSelectionPayload, subscribeSeasonSelection } from '../../../../utils/seasonSelection';
import { getSeasonConfig } from '../../../../utils/matchTypeCatalog';

const STATUS_OPTIONS = [
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready', label: 'Ready' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const formatLocalDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
};

const getMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const toStoredDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(`${dateString}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  return STATUS_OPTIONS.some((option) => option.value === status) ? status : 'preparing';
};

const normalizeOpponentOptions = (vsTeams = []) => (
  (vsTeams || [])
    .map((team, index) => {
      const name = String(team?.name || team?.teamName || team?.label || team || '').trim();
      if (!name) return null;
      return {
        id: String(team?.id || team?.teamId || `${String(team?.region || 'opponent').toLowerCase()}-${name.toLowerCase()}-${index}`),
        name,
        teamId: team?.teamId || team?.id || '',
        label: team?.label || '',
        shortName: team?.shortName || team?.tag || '',
        displayName: team?.displayName || '',
        aliases: [
          ...(Array.isArray(team?.aliases) ? team.aliases : []),
          ...(Array.isArray(team?.altNames) ? team.altNames : []),
        ],
        region: String(team?.region || '').trim(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name))
);

const normalizeCompetitionOptions = (seasonConfig = {}) => {
  const fromCatalog = (seasonConfig.matchTypeCatalog || [])
    .filter((item) => item?.active !== false)
    .map((item, index) => {
      const name = String(item?.label || item?.name || item?.MatchType || '').trim();
      if (!name) return null;
      return {
        id: String(item?.id || `competition-${name.toLowerCase()}-${index}`),
        name,
        category: String(item?.category || '').trim(),
        region: String(item?.region || '').trim(),
      };
    })
    .filter(Boolean);

  if (fromCatalog.length) {
    return fromCatalog.sort((a, b) => a.name.localeCompare(b.name));
  }

  return (seasonConfig.matchTypes || [])
    .map((type, index) => {
      const name = String(type?.label || type?.name || type || '').trim();
      if (!name) return null;
      return {
        id: `competition-${name.toLowerCase()}-${index}`,
        name,
        category: '',
        region: '',
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const normalizeRoom = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    opponentId: data.opponentId || '',
    opponentName: data.opponentName || '',
    competitionId: data.competitionId || '',
    competitionName: data.competitionName || '',
    matchDate: formatLocalDate(data.matchDate),
    format: data.format || 'BO5',
    status: normalizeStatus(data.status),
    preparationTasks: Array.isArray(data.preparationTasks) ? data.preparationTasks : [],
    winConditions: Array.isArray(data.winConditions) ? data.winConditions : [],
    notes: data.notes || '',
    createdBy: data.createdBy || '',
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
};

const sortMatchRooms = (rooms = []) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const nowMs = now.getTime();

  return [...rooms].sort((a, b) => {
    const aMs = getMillis(a.matchDate);
    const bMs = getMillis(b.matchDate);
    const aUpcoming = aMs >= nowMs;
    const bUpcoming = bMs >= nowMs;

    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;
    if (aUpcoming && bUpcoming) return aMs - bMs;
    return bMs - aMs;
  });
};

export const getStatusLabel = (value) => (
  STATUS_OPTIONS.find((option) => option.value === normalizeStatus(value))?.label || 'Preparing'
);

export const useMatchRooms = ({ teamId, uid }) => {
  const [seasonId, setSeasonId] = useState(null);
  const [seasonYear, setSeasonYear] = useState(null);
  const [seasonLabel, setSeasonLabel] = useState('');
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [seasonError, setSeasonError] = useState('');
  const [matchRooms, setMatchRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState('');
  const [opponentOptions, setOpponentOptions] = useState([]);
  const [competitionOptions, setCompetitionOptions] = useState([]);

  useEffect(() => {
    if (!teamId) {
      setSeasonId(null);
      setSeasonYear(null);
      setSeasonLabel('');
      return undefined;
    }

    let isMounted = true;
    setSeasonLoading(true);
    setSeasonError('');

    const applySeason = (nextSeasonId, payload = {}) => {
      const resolvedSeasonId = nextSeasonId ? String(nextSeasonId) : null;
      const resolvedYear = payload?.seasonYear ? String(payload.seasonYear) : null;
      setSeasonId(resolvedSeasonId);
      setSeasonYear(resolvedYear);
      setSeasonLabel(resolvedYear ? `${resolvedYear} Season` : (resolvedSeasonId || ''));
    };

    const storedPayload = getSeasonSelectionPayload(teamId);
    const storedSeason = getSeasonSelection(teamId);
    if (storedSeason) {
      applySeason(storedSeason, storedPayload);
      setSeasonLoading(false);
    } else {
      fetchActiveSeasonDoc(teamId)
        .then(({ seasonId: activeSeasonId, year }) => {
          if (!isMounted) return;
          applySeason(activeSeasonId, { seasonYear: year });
        })
        .catch((error) => {
          console.error('Failed to resolve Match Room season', error);
          if (!isMounted) return;
          setSeasonId(null);
          setSeasonYear(null);
          setSeasonLabel('');
          setSeasonError('Select or configure a season before creating a Match Room.');
        })
        .finally(() => {
          if (isMounted) setSeasonLoading(false);
        });
    }

    const unsubscribe = subscribeSeasonSelection(teamId, (selectedSeasonId, payload) => {
      if (!isMounted) return;
      applySeason(selectedSeasonId, payload);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [teamId]);

  useEffect(() => {
    if (!teamId || !seasonId) {
      setMatchRooms([]);
      setRoomsLoading(false);
      return undefined;
    }

    setRoomsLoading(true);
    setRoomsError('');
    const roomsRef = collection(db, `teams/${teamId}/seasons/${seasonId}/matchRooms`);
    const unsubscribe = onSnapshot(
      roomsRef,
      (snapshot) => {
        const rooms = snapshot.docs.map(normalizeRoom);
        setMatchRooms(sortMatchRooms(rooms));
        setRoomsLoading(false);
      },
      (error) => {
        console.error('Failed to load Match Rooms', error);
        setRoomsError('Unable to load Match Rooms.');
        setRoomsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [teamId, seasonId]);

  useEffect(() => {
    if (!teamId || !seasonId) {
      setOpponentOptions([]);
      setCompetitionOptions([]);
      setConfigLoading(false);
      return undefined;
    }

    let isMounted = true;
    setConfigLoading(true);
    setConfigError('');

    getDoc(doc(db, `teams/${teamId}/configurations/stats`))
      .then((snapshot) => {
        if (!isMounted) return;
        const config = snapshot.exists() ? snapshot.data() : null;
        const seasonConfig = getSeasonConfig(config, seasonYear || seasonId);
        setOpponentOptions(normalizeOpponentOptions(seasonConfig.vsTeams));
        setCompetitionOptions(normalizeCompetitionOptions(seasonConfig));
      })
      .catch((error) => {
        console.error('Failed to load Match Room options', error);
        if (!isMounted) return;
        setOpponentOptions([]);
        setCompetitionOptions([]);
        setConfigError('Unable to load configured opponents and competitions.');
      })
      .finally(() => {
        if (isMounted) setConfigLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [teamId, seasonId, seasonYear]);

  const roomsPath = useMemo(() => (
    teamId && seasonId ? `teams/${teamId}/seasons/${seasonId}/matchRooms` : ''
  ), [teamId, seasonId]);

  const createMatchRoom = useCallback(async ({ opponentId, competitionId, matchDate, format }) => {
    if (!roomsPath || !uid) throw new Error('Match Room season is not available.');

    const opponent = opponentOptions.find((option) => option.id === opponentId);
    const competition = competitionOptions.find((option) => option.id === competitionId);
    if (!opponent || !competition || !matchDate || !format) {
      throw new Error('Opponent, competition, match date, and format are required.');
    }

    const docRef = await addDoc(collection(db, roomsPath), {
      opponentId: opponent.id,
      opponentName: opponent.name,
      competitionId: competition.id,
      competitionName: competition.name,
      matchDate: toStoredDate(matchDate),
      format,
      status: 'preparing',
      preparationTasks: [],
      winConditions: [],
      notes: '',
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    toast.success('Match Room created.');
    return docRef.id;
  }, [competitionOptions, opponentOptions, roomsPath, uid]);

  const updateMatchRoom = useCallback(async (roomId, patch) => {
    if (!roomsPath || !roomId) throw new Error('Match Room is not available.');

    const nextPatch = { ...patch, updatedAt: serverTimestamp() };
    if (patch.opponentId) {
      const opponent = opponentOptions.find((option) => option.id === patch.opponentId);
      if (opponent) {
        nextPatch.opponentId = opponent.id;
        nextPatch.opponentName = opponent.name;
      }
    }
    if (patch.competitionId) {
      const competition = competitionOptions.find((option) => option.id === patch.competitionId);
      if (competition) {
        nextPatch.competitionId = competition.id;
        nextPatch.competitionName = competition.name;
      }
    }
    if (patch.matchDate !== undefined) {
      nextPatch.matchDate = toStoredDate(patch.matchDate);
    }
    if (patch.status) {
      nextPatch.status = normalizeStatus(patch.status);
    }

    await updateDoc(doc(db, roomsPath, roomId), nextPatch);
    toast.success('Match Room updated.');
  }, [competitionOptions, opponentOptions, roomsPath]);

  const deleteMatchRoom = useCallback(async (roomId) => {
    if (!roomsPath || !roomId) throw new Error('Match Room is not available.');
    await deleteDoc(doc(db, roomsPath, roomId));
    toast.success('Match Room deleted.');
  }, [roomsPath]);

  const updatePreparationTasks = useCallback(async (roomId, preparationTasks) => {
    if (!roomsPath || !roomId) throw new Error('Match Room is not available.');
    await updateDoc(doc(db, roomsPath, roomId), {
      preparationTasks,
      updatedAt: serverTimestamp(),
    });
  }, [roomsPath]);

  const updateWinConditions = useCallback(async (roomId, winConditions) => {
    if (!roomsPath || !roomId) throw new Error('Match Room is not available.');
    await updateDoc(doc(db, roomsPath, roomId), {
      winConditions,
      updatedAt: serverTimestamp(),
    });
  }, [roomsPath]);

  const updateNotes = useCallback(async (roomId, notes) => {
    if (!roomsPath || !roomId) throw new Error('Match Room is not available.');
    await updateDoc(doc(db, roomsPath, roomId), {
      notes,
      updatedAt: serverTimestamp(),
    });
  }, [roomsPath]);

  return {
    matchRooms,
    roomsLoading,
    roomsError,
    seasonId,
    seasonYear,
    seasonLabel,
    seasonLoading,
    seasonError,
    configLoading,
    configError,
    opponentOptions,
    competitionOptions,
    statusOptions: STATUS_OPTIONS,
    createMatchRoom,
    updateMatchRoom,
    deleteMatchRoom,
    updatePreparationTasks,
    updateWinConditions,
    updateNotes,
  };
};
